<template>
  <div class="flex gap-2">
    <VaIconMenuCollapsed
      class="cursor-pointer"
      :class="{ 'x-flip': !isSidebarMinimized }"
      :color="collapseIconColor"
      @click="isSidebarMinimized = !isSidebarMinimized"
    />

    <nav class="flex items-center">
      <VaBreadcrumbs :key="route.path">
        <VaBreadcrumbsItem label="Home" :to="{ name: 'home' }" />
        <VaBreadcrumbsItem
          v-for="item in items"
          :key="`${item.label}-${item.to}`"
          :label="item.label"
          @click="handleBreadcrumbClick(item)"
        />
      </VaBreadcrumbs>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import VaIconMenuCollapsed from '../icons/VaIconMenuCollapsed.vue'
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '../../stores/global-store'
import NavigationRoutes from '../sidebar/NavigationRoutes'

const { isSidebarMinimized } = storeToRefs(useGlobalStore())

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

type BreadcrumbNavigationItem = {
  label: string
  to: string
  hasChildren: boolean
}

const findRouteName = (name: string) => {
  const traverse = (routers: any[]): string => {
    for (const router of routers) {
      if (router.name === name) {
        return router.displayName
      }
      if (router.children) {
        const result = traverse(router.children)
        if (result) {
          return result
        }
      }
    }
    return ''
  }

  return traverse(NavigationRoutes.routes)
}

const items = computed(() => {
  const result: { label: string; to: string; hasChildren: boolean }[] = []

  route.matched.forEach((matchedRoute) => {
    // Handle user-details route specially
    if (matchedRoute.name === 'user-details') {
      const itemType = (route.query.type as string) || ''
      const itemName = (route.query.name as string) || ''

      // Add the parent page (fanpage, group, etc.) based on itemType
      if (itemType) {
        const parentLabelKey = findRouteName(itemType)
        if (parentLabelKey) {
          result.push({
            label: t(parentLabelKey),
            to: `/${itemType}`,
            hasChildren: false,
          })
        }
      }

      // Add the item name or "User Details"
      if (itemName) {
        result.push({
          label: itemName,
          to: route.path,
          hasChildren: false,
        })
      } else {
        result.push({
          label: 'User Details',
          to: route.path,
          hasChildren: false,
        })
      }
      return
    }

    // Handle other routes
    const labelKey = findRouteName(matchedRoute.name as string)
    if (!labelKey) {
      return
    }
    result.push({
      label: t(labelKey),
      to: matchedRoute.path,
      hasChildren: matchedRoute.children && matchedRoute.children.length > 0,
    })
  })

  return result
})

const { getColor } = useColors()

const collapseIconColor = computed(() => getColor('secondary'))

const handleBreadcrumbClick = (item: BreadcrumbNavigationItem) => {
  if (!item.hasChildren && item.to) {
    // Use nextTick to ensure DOM is ready before navigation
    router.push(item.to).catch((err) => {
      // Ignore navigation errors (e.g., navigating to the same route)
      if (err.name !== 'NavigationDuplicated') {
        console.error('Navigation error:', err)
      }
    })
  }
}
</script>

<style lang="scss" scoped>
.x-flip {
  transform: scaleX(-100%);
}

:deep(.va-breadcrumbs) {
  * {
    cursor: pointer !important;
  }
}

:deep(.va-breadcrumbs-item) {
  cursor: pointer !important;

  * {
    cursor: pointer !important;
  }
}
</style>
